// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import resetReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ResetReCaptcha";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import { StateModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { controlsContainerStyles } from "@insite/content-library/Widgets/ProductList/ProductListControlsContainer";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Select from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import Checkbox from "@store/MobiusOverrides/Checkbox/Checkbox";
import getProvinces from "@store/Framework/Common/Utilities/getProvinces";
import { createProfessionalAccount } from "@store/Framework/Services/ProfessionalAccountService";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import ReCaptcha from "@store/Overrides/Components/ReCaptcha";
import { formGridProps } from "@store/Themes/common/props/form";
import scrollToId from "@toolkit/Framework/Common/Utilities/scrollToId";
import { useCurrentCountries } from "@toolkit/Framework/Store/Data/Countries/CountriesHooks";
import React, { FC, useEffect, useRef, useState } from "react";
import { css } from "styled-components";
import ReCAPTCHA from "react-google-recaptcha";

const enum businessTypeFields {
    value = "value",
    label = "label",
}

interface BusinessTypeFields {
    fields: {
        [businessTypeFields.value]: string;
        [businessTypeFields.label]: string;
    };
}

const enum fields {
    successMessage = "successMessage",
    errorMessage = "errorMessage",
    businessType = "businessType",
}

export interface CreateProfessionalAccountProps extends WidgetProps {
    fields: {
        [fields.successMessage]: string;
        [fields.errorMessage]: string;
        [fields.businessType]: BusinessTypeFields[];
    };
}

export interface CreateProfessionalAccountStyles {
    errorMessage?: InfosBoxProps;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    gridItemFull?: GridItemProps;
    gridItemSmall?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    successMessage?: InfosBoxProps;
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    messagesWrapper?: InjectableCss;
}

const createProfessionalAccountStyles: CreateProfessionalAccountStyles = {
    successMessage: { type: "success" },
    errorMessage: { type: "error" },
    gridContainer: formGridProps.gridContainer,
    gridItem: { width: [12, 6, 6, 6, 6] },
    gridItemFull: { width: 12 },
    gridItemSmall: { width: [12, 4, 4, 4, 4] },
    submitButton: { variant: "primary" },
    messagesWrapper: {
        css: css`
            margin-bottom: 2em;
        `,
    },
};

const styles = createProfessionalAccountStyles;
const StyledForm = getStyledWrapper("form");

const CreateProfessionalAccount: FC<CreateProfessionalAccountProps> = ({ fields }) => {
    const recaptchaLocation = "CreateProfessionalAccount";
    const messageBoxId = "infosBoxContainer";
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const recaptchaRef = useRef<ReCAPTCHA | null>(null);
    const countries = useCurrentCountries();
    const formCountries = countries?.length
        ? [
              ...countries,
              {
                  abbreviation: "CA",
                  id: "CA",
                  name: "Canada",
                  states: getProvinces(),
                  uri: "",
                  properties: {},
              },
          ]
        : [];
    const defaultCountry = "US";
    const reCaptchaEnabledForCreateAccount = useAppSelector(
        state => getSettingsCollection(state).websiteSettings.reCaptchaEnabledForCreateAccount,
    );

    const firstNameLabel = translate("First Name");
    const lastNameLabel = translate("Last Name");
    const emailLabel = translate("Email Address");
    const addressLine1Label = translate("Address Line 1");
    const addressLine2Label = translate("Address Line 2");
    const companyNameLabel = translate("Company Name");
    const businessTypeLabel = translate("Primary Business Type");
    const cityLabel = translate("City");
    const stateLabel = translate("State");
    const provinceLabel = translate("Province");
    const zipCodeLabel = translate("Zip Code");
    const phoneLabel = translate("Business Phone");
    const companyWebsiteLabel = translate("Company Website");
    const emserAccountNumberLabel = translate("Emser Account Number (if any)");
    const newsletterLabel = translate("Sign me up for the eNewsletter");
    const countryLabel = translate("Country");

    const firstNameErrorMessage = siteMessage("Field_Required", firstNameLabel).toString();
    const lastNameErrorMessage = siteMessage("Field_Required", lastNameLabel).toString();
    const emailErrorDefaultMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required").toString();
    const companyNameErrorMessage = siteMessage("Field_Required", companyNameLabel).toString();
    const phoneInvalidMessage = translate("The accepted Phone format is 1234567890");
    const addressLine1ErrorMessage = siteMessage("Field_Required", addressLine1Label).toString();
    const businessTypeErrorMessage = siteMessage("Field_Required", businessTypeLabel).toString();
    const cityErrorMessage = siteMessage("Field_Required", cityLabel).toString();
    const stateErrorMessage = siteMessage("Field_Required", stateLabel).toString();
    const provinceErrorMessage = siteMessage("Field_Required", provinceLabel).toString();
    const zipCodeErrorMessage = siteMessage("Field_Required", zipCodeLabel).toString();
    const zipCodeFormatErrorMessage = translate("The accepted Zip Code formats are 12345 or A1A1A1");
    const accountNumberErrorMessage = translate("The Account Number must be numeric and has a maximum of 8 digits");
    const countryErrorMessage = siteMessage("Field_Invalid", countryLabel).toString();

    const [firstName, setFirstName] = useState("");
    const [firstNameError, setFirstNameError] = useState("");
    const [lastName, setLastName] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [emailAddress, setEmailAddress] = useState("");
    const [emailAddressError, setEmailAddressError] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [companyNameError, setCompanyNameError] = useState("");
    const [addressLine1, setAddressLine1] = useState("");
    const [addressLine1Error, setAddressLine1Error] = useState("");
    const [addressLine2, setAddressLine2] = useState("");
    const [companyWebsite, setCompanyWebsite] = useState("");
    const [emserAccountNumber, setEmserAccountNumber] = useState("");
    const [accountNumberError, setAccountNumberError] = useState("");
    const [phone, setPhone] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [zipCodeError, setZipCodeError] = useState("");
    const [city, setCity] = useState("");
    const [cityError, setCityError] = useState("");
    const [country, setCountry] = useState(defaultCountry);
    const [countryError, setCountryError] = useState("");
    const [businessType, setBusinessType] = useState("");
    const [businessTypeError, setBusinessTypeError] = useState("");
    const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(true);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [state, setState] = useState("");
    const [statesList, setStatesList] = useState<Array<StateModel>>([]);
    const [stateError, setStateError] = useState("");
    const [displaySuccessMessage, setDisplaySuccessMessage] = useState(false);
    const [displayErrorMessage, setDisplayErrorMessage] = useState(false);

    useEffect(() => {
        if (!countries) {
            dispatch(loadCurrentCountries());
        }
    }, [countries, dispatch]);

    useEffect(() => {
        setStatesList(formCountries.find(countryItem => countryItem.abbreviation === country)?.states || []);
    }, [country, countries]);

    const validateFirstName = (value: string) => {
        const msg = !value.trim() ? firstNameErrorMessage : "";
        setFirstNameError(msg);
    };

    const validateLastName = (value: string) => {
        const msg = !value.trim() ? lastNameErrorMessage : "";
        setLastNameError(msg);
    };

    const validateEmailAddress = (value: string) => {
        const msg = !value
            ? emailErrorDefaultMessage
            : validateEmail(value)
              ? ""
              : siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail").toString();
        setEmailAddressError(msg);
    };

    const validateAddressLine1 = (value: string) => {
        const msg = !value.trim() ? addressLine1ErrorMessage : "";
        setAddressLine1Error(msg);
    };

    const validateCompanyName = (value: string) => {
        const msg = !value.trim() ? companyNameErrorMessage : "";
        setCompanyNameError(msg);
    };

    const validatePhoneNumber = (value: string) => {
        const trimmedValue = value.trim();
        const phoneRegex = /^\d{10}$/g;
        const msg = trimmedValue && !phoneRegex.test(trimmedValue) ? phoneInvalidMessage : "";
        setPhoneError(msg);
    };

    const validateZipCode = (value: string) => {
        const trimmedValue = value.trim();
        const zipRegex = /(^\d{5}$)|(^([a-zA-Z]\d){3}$)/g;
        let msg = !trimmedValue ? zipCodeErrorMessage : "";

        if (!(trimmedValue.length === 5 || trimmedValue.length === 6) || !zipRegex.test(trimmedValue)) {
            msg = zipCodeFormatErrorMessage;
        }

        setZipCodeError(msg);
    };

    const validateState = (value: string) => {
        let msg = "";

        if (!value.trim()) {
            msg = country === "CA" ? provinceErrorMessage : stateErrorMessage;
        }

        setStateError(msg);
    };

    const validateCountry = (value: string) => {
        const msg = !value.trim() ? countryErrorMessage : "";
        setCountryError(msg);
    };

    const validateCity = (value: string) => {
        const msg = !value.trim() ? cityErrorMessage : "";
        setCityError(msg);
    };

    const validateBusinessType = (value: string) => {
        const msg = !value.trim() ? businessTypeErrorMessage : "";
        setBusinessTypeError(msg);
        return msg;
    };

    const validateAccountNumber = (value: string) => {
        const trimmedValue = value.trim();
        const accountNumberRegex = /^\d{0,8}$/g;
        const msg = trimmedValue.length > 0 && !accountNumberRegex.test(trimmedValue) ? accountNumberErrorMessage : "";
        setAccountNumberError(msg);
        return msg;
    };

    const resetForm = () => {
        setFirstName("");
        setLastName("");
        setEmailAddress("");
        setPhone("");
        setBusinessType("");
        setCompanyName("");
        setCompanyWebsite("");
        setAddressLine1("");
        setAddressLine2("");
        setCity("");
        setState("");
        setCountry("");
        setZipCode("");
        setEmserAccountNumber("");
        setSubscribeToNewsletter(true);
        resetReCaptcha({ location: recaptchaLocation });
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setDisplaySuccessMessage(false);
        setDisplayErrorMessage(false);

        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        validateFirstName(firstName);
        validateLastName(lastName);
        validateEmailAddress(emailAddress);
        validateCompanyName(companyName);
        validateBusinessType(businessType);
        validateAddressLine1(addressLine1);
        validateCity(city);
        validateState(state);
        validateCountry(country);
        validateZipCode(zipCode);
        validatePhoneNumber(phone);
        validateAccountNumber(emserAccountNumber);

        if (
            firstNameError !== "" ||
            lastNameError !== "" ||
            emailAddressError !== "" ||
            companyNameError !== "" ||
            businessTypeError !== "" ||
            addressLine1Error !== "" ||
            cityError !== "" ||
            stateError !== "" ||
            zipCodeError !== "" ||
            countryError !== "" ||
            phoneError !== "" ||
            accountNumberError !== ""
        ) {
            return;
        }

        let isReCaptchaValid = false;
        await dispatch(
            validateReCaptcha({
                location: recaptchaLocation,
                recaptchaRef,
                onSuccess: isValid => {
                    isReCaptchaValid = isValid;
                },
            }),
        );

        if (!isReCaptchaValid) {
            return;
        }

        setIsLoading(true);

        createProfessionalAccount({
            firstName,
            lastName,
            email: emailAddress,
            phoneNumber: phone,
            primaryBusinessType: businessType,
            companyName,
            companyWebsite,
            addressLine1,
            addressLine2,
            city,
            country,
            stateOrProvince: state,
            zip: zipCode,
            accountNumber: emserAccountNumber || null,
            subscribeToNewsletter,
        })
            .then(() => {
                setDisplaySuccessMessage(true);
                resetForm();
            })
            .catch(() => {
                setDisplayErrorMessage(true);
            })
            .finally(() => {
                setIsLoading(false);
                setIsSubmitted(false);
                scrollToId(messageBoxId);
            });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {(displaySuccessMessage || displayErrorMessage) && (
                <StyledWrapper id={messageBoxId} {...styles.messagesWrapper}>
                    {displaySuccessMessage && <InfosBox {...styles.successMessage}>{fields.successMessage}</InfosBox>}
                    {displayErrorMessage && <InfosBox {...styles.errorMessage}>{fields.errorMessage}</InfosBox>}
                </StyledWrapper>
            )}
            <LoadingOverlay {...styles.loadingOverlay} loading={isLoading}>
                <StyledForm onSubmit={submitHandler} noValidate>
                    <GridContainer {...styles.gridContainer}>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={firstNameLabel}
                                name="firstName"
                                type="text"
                                required
                                value={firstName}
                                onChange={e => {
                                    setFirstName(e.currentTarget.value);
                                    validateFirstName(e.currentTarget.value);
                                }}
                                error={isSubmitted && firstNameError}
                                autoComplete="given-name"
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={lastNameLabel}
                                name="lastName"
                                type="text"
                                required
                                value={lastName}
                                onChange={e => {
                                    setLastName(e.currentTarget.value);
                                    validateLastName(e.currentTarget.value);
                                }}
                                error={isSubmitted && lastNameError}
                                autoComplete="family-name"
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemFull}>
                            <TextField
                                label={emailLabel}
                                name="email"
                                type="email"
                                required
                                value={emailAddress}
                                onChange={e => {
                                    setEmailAddress(e.currentTarget.value);
                                    validateEmailAddress(e.currentTarget.value);
                                }}
                                error={isSubmitted && emailAddressError}
                                autoComplete="email"
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={phoneLabel}
                                name="phone"
                                type="tel"
                                inputMode="tel"
                                value={phone}
                                onChange={e => {
                                    setPhone(e.currentTarget.value);
                                    validatePhoneNumber(e.currentTarget.value);
                                }}
                                autoComplete="tel"
                                error={isSubmitted && phoneError}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <Select
                                label={businessTypeLabel}
                                name="primaryBusinessType"
                                required
                                value={businessType}
                                onChange={e => {
                                    setBusinessType(e.currentTarget.value);
                                    validateBusinessType(e.currentTarget.value);
                                }}
                                error={isSubmitted && businessTypeError}
                            >
                                <option value="">{translate("Please select...")}</option>
                                {fields.businessType.map(({ fields: { label, value } }) => (
                                    <option key={value} value={value}>
                                        {label || value}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={companyNameLabel}
                                name="companyName"
                                type="text"
                                required
                                value={companyName}
                                onChange={e => {
                                    setCompanyName(e.currentTarget.value);
                                    validateCompanyName(e.currentTarget.value);
                                }}
                                error={isSubmitted && companyNameError}
                                autoComplete="companyName"
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={companyWebsiteLabel}
                                name="companyWebsite"
                                type="text"
                                value={companyWebsite}
                                onChange={e => setCompanyWebsite(e.currentTarget.value)}
                                autoComplete="website"
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemFull}>
                            <TextField
                                label={addressLine1Label}
                                name="addressLine1"
                                type="text"
                                value={addressLine1}
                                required
                                onChange={e => {
                                    setAddressLine1(e.currentTarget.value);
                                    validateAddressLine1(e.currentTarget.value);
                                }}
                                error={isSubmitted && addressLine1Error}
                                autoComplete="address-line1"
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemFull}>
                            <TextField
                                label={addressLine2Label}
                                name="addressLine2"
                                type="text"
                                value={addressLine2}
                                onChange={e => setAddressLine2(e.currentTarget.value)}
                                autoComplete="address-line2"
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemSmall}>
                            <TextField
                                label={cityLabel}
                                name="city"
                                type="text"
                                value={city}
                                required
                                onChange={e => {
                                    setCity(e.currentTarget.value);
                                    validateCity(e.currentTarget.value);
                                }}
                                error={isSubmitted && cityError}
                                autoComplete="city"
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemSmall}>
                            <Select
                                label={countryLabel}
                                name="country"
                                onChange={e => {
                                    setCountry(e.currentTarget.value);
                                    validateCountry(e.currentTarget.value);
                                }}
                                value={country}
                                required
                                error={isSubmitted && countryError}
                                autoComplete="country"
                            >
                                <option value="">{translate("Please select...")}</option>
                                {formCountries?.map(country => {
                                    return (
                                        <option key={country.abbreviation} value={country.abbreviation}>
                                            {country.name}
                                        </option>
                                    );
                                })}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.gridItemSmall}>
                            <Select
                                label={country === "CA" ? provinceLabel : stateLabel}
                                name="stateOrProvince"
                                onChange={e => {
                                    setState(e.currentTarget.value);
                                    validateState(e.currentTarget.value);
                                }}
                                value={state}
                                required
                                error={isSubmitted && stateError}
                                autoComplete="state"
                                disabled={!statesList.length}
                            >
                                <option value="">{translate("Please select...")}</option>
                                {statesList.map(state => {
                                    return (
                                        <option key={state.abbreviation} value={state.abbreviation}>
                                            {state.name}
                                        </option>
                                    );
                                })}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.gridItemSmall}>
                            <TextField
                                label={zipCodeLabel}
                                name="zip"
                                type="text"
                                inputMode="numeric"
                                required
                                value={zipCode}
                                onChange={e => {
                                    setZipCode(e.currentTarget.value);
                                    validateZipCode(e.currentTarget.value);
                                }}
                                error={isSubmitted && zipCodeError}
                                autoComplete="postal-code"
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemSmall}>
                            <TextField
                                label={emserAccountNumberLabel}
                                name="emserAccountNumber"
                                type="text"
                                inputMode="numeric"
                                value={emserAccountNumber}
                                onChange={e => {
                                    setEmserAccountNumber(e.currentTarget.value);
                                    validateAccountNumber(e.currentTarget.value);
                                }}
                                error={isSubmitted && accountNumberError}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemFull}>
                            <Checkbox
                                checked={subscribeToNewsletter}
                                onChange={(e, value) => setSubscribeToNewsletter(value)}
                            >
                                {newsletterLabel}
                            </Checkbox>
                        </GridItem>
                        {reCaptchaEnabledForCreateAccount && (
                            <GridItem {...styles.gridItemFull}>
                                <ReCaptcha recaptchaRef={recaptchaRef} location={recaptchaLocation} />
                            </GridItem>
                        )}
                    </GridContainer>
                    <FormActions>
                        <Button {...styles.submitButton} type="submit">
                            {translate("Submit")}
                        </Button>
                    </FormActions>
                </StyledForm>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: CreateProfessionalAccount,
    definition: {
        group: "Create Account",
        displayName: "Create Professional Account Form",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.successMessage,
                displayName: "Success Message",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "Thank you! Your form has been submitted successfully. We will get back to you shortly.",
                isRequired: true,
            },
            {
                name: fields.errorMessage,
                displayName: "Generic Error Message",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "We could not submit your form at this time, please try again later or call us.",
                isRequired: true,
            },
            {
                name: fields.businessType,
                displayName: "Business Type",
                editorTemplate: "ListField",
                defaultValue: [
                    {
                        fields: {
                            value: "ARCHITECT_DESIGNER",
                            label: "Architect/Designer",
                        },
                    },
                    {
                        fields: {
                            value: "HOME_BUILDER",
                            label: "Homebuilder",
                        },
                    },
                    {
                        fields: {
                            value: "COMMERCIAL_DEVELOPER",
                            label: "Commercial Developer",
                        },
                    },
                    {
                        fields: {
                            value: "CONTRACTOR",
                            label: "Contractor",
                        },
                    },
                    {
                        fields: {
                            value: "FABRICATOR",
                            label: "Fabricator",
                        },
                    },
                    {
                        fields: {
                            value: "HOMEOWNER",
                            label: "Homeowner",
                        },
                    },
                    {
                        fields: {
                            value: "RESIDENTIAL_BUILDER",
                            label: "Residential Builder",
                        },
                    },
                    {
                        fields: {
                            value: "RETAILER",
                            label: "Retailer",
                        },
                    },
                    {
                        fields: {
                            value: "ENGINEER",
                            label: "Engineer",
                        },
                    },
                    {
                        fields: {
                            value: "GOVERNMENT",
                            label: "Government",
                        },
                    },
                    {
                        fields: {
                            value: "CONSUMER",
                            label: "Consumer",
                        },
                    },
                    {
                        fields: {
                            value: "HOTEL_FLAG_BRANDS",
                            label: "Hotel Flag/Brands",
                        },
                    },
                    {
                        fields: {
                            value: "BUYING_GROUP",
                            label: "Buying Group",
                        },
                    },
                    {
                        fields: {
                            value: "TRADES",
                            label: "Trades",
                        },
                    },
                    {
                        fields: {
                            value: "DEALER",
                            label: "Dealer",
                        },
                    },
                    {
                        fields: {
                            value: "DESIGNER",
                            label: "Designer",
                        },
                    },
                    {
                        fields: {
                            value: "DISTRIBUTOR",
                            label: "Distributor",
                        },
                    },
                    {
                        fields: {
                            value: "OTHER",
                            label: "Other",
                        },
                    },
                ],
                getDisplay: item => item.fields.label || item.fields.value,
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: businessTypeFields.value,
                        defaultValue: "",
                        displayName: "value",
                        editorTemplate: "TextField",
                        isRequired: true,
                    },
                    {
                        name: businessTypeFields.label,
                        defaultValue: "",
                        displayName: "label",
                        editorTemplate: "TextField",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
